import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useCallback,
  useRef,
} from "react";
import { apiFetch } from "../lib/apiFetch";
import type { AthenaActiveEncounter } from "../api/athena";

/* ============================================
   TYPE EXPORTS USED BY THE UI
   ============================================ */

export interface ProviderInfo {
  provider: string;
  count: number;
}

export interface AthenaProviderInfo {
  name: string;
  count: number;
}

export interface ClaimInfo {
  patient_name: string;
  patient_id: string;
  appointment_date: string;
  department: string;
}

export interface ClaimDetail {
  billing: any;
  summary: any;
  folder_path: string;
  updated_at: string;
  patient_name: string;
}

type JsonLike = Record<string, unknown> | unknown[] | string | number | boolean | null;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const readJsonSafe = async (res: Response): Promise<JsonLike> => {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as JsonLike;
  } catch {
    return text;
  }
};

const toProviderInfoArray = (payload: unknown): ProviderInfo[] => {
  if (Array.isArray(payload)) {
    return payload
      .map((item) => {
        if (typeof item === "string") {
          return { provider: item, count: 0 };
        }

        if (!isRecord(item) || typeof item.provider !== "string") {
          return null;
        }

        const countRaw = item.count;
        const count =
          typeof countRaw === "number"
            ? countRaw
            : typeof countRaw === "string"
            ? Number(countRaw)
            : 0;

        return { provider: item.provider, count: Number.isFinite(count) ? count : 0 };
      })
      .filter((item): item is ProviderInfo => item !== null);
  }

  if (isRecord(payload)) {
    if (Array.isArray(payload.providers)) {
      return toProviderInfoArray(payload.providers);
    }
    if (Array.isArray(payload.items)) {
      return toProviderInfoArray(payload.items);
    }
    if (Array.isArray(payload.data)) {
      return toProviderInfoArray(payload.data);
    }
  }

  return [];
};

const toClaimInfoArray = (payload: unknown): ClaimInfo[] => {
  const source = Array.isArray(payload)
    ? payload
    : isRecord(payload) && Array.isArray(payload.items)
    ? payload.items
    : isRecord(payload) && Array.isArray(payload.data)
    ? payload.data
    : isRecord(payload) && Array.isArray(payload.claims)
    ? payload.claims
    : [];

  return source
    .map((item) => {
      if (!isRecord(item)) return null;

      const patient_name =
        typeof item.patient_name === "string"
          ? item.patient_name
          : typeof item.patientName === "string"
          ? item.patientName
          : "";
      const patient_id =
        typeof item.patient_id === "string"
          ? item.patient_id
          : typeof item.patientId === "string"
          ? item.patientId
          : "";
      const appointment_date =
        typeof item.appointment_date === "string"
          ? item.appointment_date
          : typeof item.appointmentDate === "string"
          ? item.appointmentDate
          : "";
      const department = typeof item.department === "string" ? item.department : "";

      if (!patient_name || !patient_id || !appointment_date) return null;
      return {
        patient_name,
        patient_id,
        appointment_date,
        department,
      };
    })
    .filter((item): item is ClaimInfo => item !== null);
};

/* ============================================
   ENCOUNTER CACHE
   ============================================ */

export interface EncounterCache {
  key: string;
  encounters: AthenaActiveEncounter[];
  providers: { name: string; count: number }[];
  loadedAt: number;
  claimStatusMap?: Record<number, import("../api/athena").AthenaEncounterSummary>;
}

/* ============================================
   CONTEXT SHAPE
   ============================================ */

interface ClaimWorklistContextType {
  providers: ProviderInfo[];
  claimsByProvider: Record<string, ClaimInfo[]>;
  loading: {
    providers: boolean;
    claims: string | null;
    search: boolean;
  };
  searchResults: ClaimInfo[];
  fetchProviders: () => void;
  fetchClaimsForProvider: (provider: string) => void;
  searchClaims: (query: string) => void;
  sortClaimsByProvider: (provider: string, sortBy: 'patient_name' | 'appointment_date') => void;
  clearSearch: () => void;
  refreshWorklist: () => void;
  // Athena active worklist providers (set by ActiveWorklist after loading encounters)
  athenaProviders: AthenaProviderInfo[];
  athenaSelectedProvider: string | null;
  setAthenaProviders: (providers: AthenaProviderInfo[]) => void;
  setAthenaSelectedProvider: (provider: string | null) => void;
  // Encounter list cache — avoids re-fetching on provider filter / navigation
  encounterCache: EncounterCache | null;
  setEncounterCache: (cache: EncounterCache | null) => void;
}

const ClaimWorklistContext = createContext<
  ClaimWorklistContextType | undefined
>(undefined);

/* ============================================
   PROVIDER COMPONENT
   ============================================ */

export const ClaimWorklistProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [claimsByProvider, setClaimsByProvider] = useState<
    Record<string, ClaimInfo[]>
  >({});
  const [loading, setLoading] = useState({
    providers: false,
    claims: null as string | null,
    search: false,
  });
  const [searchResults, setSearchResults] = useState<ClaimInfo[]>([]);
  const [athenaProviders, setAthenaProviders] = useState<AthenaProviderInfo[]>([]);
  const [athenaSelectedProvider, setAthenaSelectedProvider] = useState<string | null>(null);
  const [encounterCache, setEncounterCache] = useState<EncounterCache | null>(null);
  const activeSearchRequestRef = useRef(0);

  /* -------- Fetch Provider List -------- */
  const fetchProviders = useCallback(async () => {
    if (providers.length > 0 || loading.providers) return;

    setLoading((prev) => ({ ...prev, providers: true }));

    try {
      const res = await apiFetch("/claims/claimworklist");
      const data = await readJsonSafe(res);
      if (!res.ok) {
        throw new Error(`claimworklist failed (${res.status})`);
      }
      setProviders(toProviderInfoArray(data));
    } catch (error) {
      console.error("Failed to fetch providers:", error);
      setProviders([]);
    } finally {
      setLoading((prev) => ({ ...prev, providers: false }));
    }
  }, [providers.length, loading.providers]);

  /* -------- Refresh Worklist -------- */
  const refreshWorklist = useCallback(async () => {
    setLoading(prev => ({ ...prev, providers: true }));
    try {
      const res = await apiFetch("/claims/claimworklist");
      const data = await readJsonSafe(res);
      if (!res.ok) {
        throw new Error(`claimworklist refresh failed (${res.status})`);
      }
      setProviders(toProviderInfoArray(data));
      setClaimsByProvider({}); // Clear old claims
    } catch (error) {
      console.error("Failed to refresh providers:", error);
      setProviders([]);
    } finally {
      setLoading(prev => ({ ...prev, providers: false }));
    }
  }, []);

  /* -------- Fetch Claims for a Provider -------- */
  const fetchClaimsForProvider = useCallback(
    async (provider: string) => {
      if (claimsByProvider[provider] || loading.claims === provider) return;

      setLoading((prev) => ({ ...prev, claims: provider }));

      try {
        const res = await apiFetch(`/claims/claimworklist/providers/${encodeURIComponent(provider)}`);
        const data = await readJsonSafe(res);
        if (!res.ok) {
          throw new Error(`provider claims failed (${res.status})`);
        }
        setClaimsByProvider((prev) => ({ ...prev, [provider]: toClaimInfoArray(data) }));
      } catch (error) {
        console.error(`Failed to fetch claims for ${provider}:`, error);
        setClaimsByProvider((prev) => ({ ...prev, [provider]: [] }));
      } finally {
        setLoading((prev) => ({ ...prev, claims: null }));
      }
    },
    [claimsByProvider, loading.claims]
  );

  /* -------- Search Claims -------- */
  const searchClaims = useCallback(async (query: string) => {
    const normalizedQuery = query.trim();
    activeSearchRequestRef.current += 1;
    const requestId = activeSearchRequestRef.current;

    if (!normalizedQuery) {
      setLoading((prev) => ({ ...prev, search: false }));
      setSearchResults([]);
      return;
    }

    setLoading((prev) => ({ ...prev, search: true }));

    try {
      const res = await apiFetch(`/claims/claimworklist/search?q=${encodeURIComponent(normalizedQuery)}`);
      const data = await readJsonSafe(res);
      if (requestId !== activeSearchRequestRef.current) return;
      if (!res.ok) {
        throw new Error(`claim search failed (${res.status})`);
      }
      setSearchResults(toClaimInfoArray(data));
    } catch (error) {
      if (requestId !== activeSearchRequestRef.current) return;
      console.error("Failed to search claims:", error);
      setSearchResults([]);
    } finally {
      if (requestId === activeSearchRequestRef.current) {
        setLoading((prev) => ({ ...prev, search: false }));
      }
    }
  }, []);

  /* -------- Sort Claims for a Provider -------- */
  const sortClaimsByProvider = useCallback(
    (provider: string, sortBy: 'patient_name' | 'appointment_date') => {
      const claims = claimsByProvider[provider];
      if (!claims) return;

      const sortedClaims = [...claims].sort((a, b) => {
        if (sortBy === 'patient_name') {
          return a.patient_name.localeCompare(b.patient_name);
        } else { // appointment_date
          const dateA = new Date(a.appointment_date).getTime();
          const dateB = new Date(b.appointment_date).getTime();
          return dateA - dateB;
        }
      });

      setClaimsByProvider((prev) => ({ ...prev, [provider]: sortedClaims }));
    },
    [claimsByProvider]
  );

  const clearSearch = useCallback(() => {
    activeSearchRequestRef.current += 1;
    setLoading((prev) => ({ ...prev, search: false }));
    setSearchResults([]);
  }, []);

  return (
    <ClaimWorklistContext.Provider
      value={{
        providers,
        claimsByProvider,
        loading,
        searchResults,
        fetchProviders,
        fetchClaimsForProvider,
        searchClaims,
        sortClaimsByProvider,
        clearSearch,
        refreshWorklist,
        athenaProviders,
        athenaSelectedProvider,
        setAthenaProviders,
        setAthenaSelectedProvider,
        encounterCache,
        setEncounterCache,
      }}
    >
      {children}
    </ClaimWorklistContext.Provider>
  );
};

/* ============================================
   HOOK
   ============================================ */

export const useClaimWorklist = () => {
  const context = useContext(ClaimWorklistContext);

  if (context === undefined) {
    throw new Error(
      "useClaimWorklist must be used within a ClaimWorklistProvider"
    );
  }

  return context;
};
